import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { useAuthStore } from '../../store/authStore';
import { toast } from 'react-toastify';

// Export the API URL for use in other services
export const API_URL = process.env.NODE_ENV === 'production' ? (process.env.REACT_APP_API_URL || '') : '';

// Create axios instance with timeout
const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  // Don't set default Content-Type here - let it be set per request
  timeout: 300000, // 5 minute timeout for file processing and OCR operations
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const { token } = useAuthStore.getState();

    if (token && token.access_token) {
      config.headers.Authorization = `Bearer ${token.access_token}`;
    }

    // Set Content-Type based on the data type
    // Don't set Content-Type for FormData - axios will set it automatically with boundary
    if (config.data instanceof FormData) {
      // Remove any Content-Type header for FormData
      // Axios will set it automatically with the proper boundary
      delete config.headers['Content-Type'];
    } else if (!config.headers['Content-Type']) {
      // Only set JSON content type if not already set and not FormData
      config.headers['Content-Type'] = 'application/json';
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh and errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest: any = error.config;

    // Handle 401/403 auth errors - but NOT for login/register endpoints
    // HTTPBearer may return 403 when no token is present, 401 when token is expired
    const responseData = error.response?.data as any;
    const isAuthError = error.response?.status === 401 ||
      (error.response?.status === 403 && responseData?.detail === 'Not authenticated');
    if (isAuthError && !originalRequest._retry) {
      const isAuthEndpoint = originalRequest.url?.includes('/auth/login') || 
                           originalRequest.url?.includes('/auth/register') ||
                           originalRequest.url?.includes('/auth/refresh');
      
      // Skip token refresh logic for auth endpoints - let them handle their own 401s
      if (isAuthEndpoint) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      const { token, clearAuth, updateToken } = useAuthStore.getState();
      
      if (token?.refresh_token) {
        try {
          // Try to refresh the token
          const refreshBaseURL = process.env.NODE_ENV === 'production' ? (process.env.REACT_APP_API_URL || '') : '';
          const response = await axios.post(
            `${refreshBaseURL}/api/v1/auth/refresh`,
            { refresh_token: token.refresh_token }
          );
          
          const newToken = response.data;
          updateToken(newToken);
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken.access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, clear auth and redirect to login
          clearAuth();
          toast.error('Session expired. Please log in again.');
          window.location.href = '/';
        }
      } else {
        // No refresh token available, clear auth
        clearAuth();
        toast.error('Authentication required. Please log in.');
        window.location.href = '/';
      }
    }

    // Handle other HTTP errors - but skip auth endpoints which handle their own errors
    if (error.response) {
      const status = error.response.status;
      const data = error.response.data as any;
      const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
                           originalRequest.url?.includes('/auth/register') ||
                           originalRequest.url?.includes('/auth/refresh') ||
                           originalRequest.url?.includes('/auth/request-password-reset') ||
                           originalRequest.url?.includes('/auth/reset-password') ||
                           (originalRequest.url?.includes('/oauth/') && originalRequest.url?.includes('/authenticate'));

      // Skip toast notifications for module push/validate endpoints — they handle their own errors
      const isModuleEndpoint = originalRequest.url?.includes('/modules/') &&
        (originalRequest.url?.includes('/push') || originalRequest.url?.includes('/validate'));

      // Skip toasts for the extracted-data autosave (background PATCH) — a
      // failed silent save must not interrupt the user with an error popup.
      const isBackgroundSave = originalRequest.method?.toLowerCase() === 'patch' &&
        originalRequest.url?.includes('/transactions/');

      // Skip toast notifications for auth endpoints - let them handle their own UI feedback
      if (!isAuthEndpoint && !isModuleEndpoint && !isBackgroundSave) {
        switch (status) {
          case 400:
            toast.error(data?.detail || 'Bad request');
            break;
          case 403:
            toast.error('Access forbidden');
            break;
          case 404:
            toast.error('Resource not found');
            break;
          case 422:
            if (data?.detail && Array.isArray(data.detail)) {
              data.detail.forEach((err: any) => {
                toast.error(`${err.loc?.join(' ')}: ${err.msg}`);
              });
            } else {
              toast.error(data?.detail || 'Validation error');
            }
            break;
          case 429:
            toast.error('Too many requests. Please try again later.');
            break;
          case 500:
            toast.error('Server error. Please try again later.');
            break;
          case 502:
            toast.error('Service temporarily unavailable. Please try again.');
            break;
          case 503:
            toast.error(data?.detail || 'Service temporarily unavailable. Please try again.');
            break;
          case 504:
            toast.error(data?.detail || 'Request timed out. Please try with a smaller file or simpler request.');
            break;
          default:
            toast.error(data?.detail || 'An error occurred');
        }
      }
    } else if (error.request) {
      // Network error or timeout - but skip for auth/module endpoints and cancelled requests
      const isAuthEndpoint = originalRequest.url?.includes('/auth/');
      const isModuleEndpoint = originalRequest.url?.includes('/modules/') &&
        (originalRequest.url?.includes('/push') || originalRequest.url?.includes('/validate'));
      const isBackgroundSave = originalRequest.method?.toLowerCase() === 'patch' &&
        originalRequest.url?.includes('/transactions/');
      const isCancelled = error.code === 'ERR_CANCELED' || error.message?.includes('canceled');

      if (!isAuthEndpoint && !isModuleEndpoint && !isBackgroundSave && !isCancelled) {
        // Check if it's a timeout error
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          toast.error('Request timed out. The server is taking too long to respond.');
        } else {
          toast.error('Network error. Please check your connection.');
        }
      }
    } else {
      // Other error - but skip for auth endpoints and cancelled requests
      const isAuthEndpoint = originalRequest.url?.includes('/auth/');
      const isCancelled = error.code === 'ERR_CANCELED' || error.message?.includes('canceled');

      if (!isAuthEndpoint && !isCancelled) {
        toast.error('An unexpected error occurred.');
      }
    }

    return Promise.reject(error);
  }
);

export default api;